const logoPath = `${process.env.PUBLIC_URL || ""}/assets/favicon.ico`;
let embeddedLogoUrl;

// Embed the favicon so the standalone invoice HTML keeps its branding.
export const getInvoiceLogoDataUrl = async () => {
  if (embeddedLogoUrl) return embeddedLogoUrl;

  try {
    const response = await fetch(logoPath);
    if (!response.ok) throw new Error("Unable to load invoice logo");

    const logoBlob = await response.blob();
    embeddedLogoUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(logoBlob);
    });
  } catch (error) {
    // Keep the app-hosted path as a graceful fallback if the asset cannot be embedded.
    embeddedLogoUrl = logoPath;
  }

  return embeddedLogoUrl;
};

export const buildInvoiceHtml = (order, logoUrl = logoPath) => {
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹ 0.00';
    return `₹ ${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '08/04/2026';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '-';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const items = order.items && order.items.length > 0 ? order.items : [
    {
      title: "Basic Plan",
      originalPrice: 5000,
      discountedPrice: 5000,
      bestPrice: 5000,
      discountPercent: 0,
      gstSlab: 0,
    }
  ];

  const itemPrice = (item) => Number(item.bestPrice ?? item.discountedPrice ?? item.originalPrice ?? 0) || 0;
  const subtotal = Number(order.totalAmount ?? items.reduce((sum, item) => sum + (Number(item.originalPrice) || itemPrice(item)), 0)) || 0;
  const catalogueDiscount = Number(order.totalDiscount ?? Math.max(0, subtotal - items.reduce((sum, item) => sum + itemPrice(item), 0))) || 0;
  const couponDiscount = Number(order.couponDiscountAmount ?? order.coupon?.discountAmount ?? 0) || 0;
  const finalAmount = Number(order.finalAmount ?? Math.max(0, subtotal - catalogueDiscount - couponDiscount)) || 0;
  const totalBestPrice = items.reduce((sum, item) => sum + itemPrice(item), 0);
  const gstBySlab = {};
  items.forEach((item) => {
    const slab = Number(item.gstSlab ?? item.gst ?? 0) || 0;
    const gross = itemPrice(item);
    const allocatedCoupon = totalBestPrice > 0 ? couponDiscount * (gross / totalBestPrice) : 0;
    const paidGross = Math.max(0, gross - allocatedCoupon);
    const gst = paidGross - (paidGross / (1 + slab / 100));
    gstBySlab[slab] = (gstBySlab[slab] || 0) + gst;
  });

  const rows = items
    .map(
      (item, index) => `
        <tr>
          <td style="text-align: center; color: #475569;">${index + 1}</td>
          <td style="font-weight: 500; color: #0f172a;">
            ${item.title || item.type || "Basic Plan"}
          </td>
          <td style="color: #475569;">${item.discountPercent !== undefined ? `${item.discountPercent}%` : "0%"}</td>
          <td style="text-align: right; font-weight: 500; color: #0f172a;">${formatCurrency(itemPrice(item))}</td>
        </tr>`
    )
    .join("");

  const formattedDate = formatDate(order.createdAt);
  const customerName = order.customerName || (order.user ? order.user.name : "Vaibhav 12345");
  const customerEmail = order.customerEmail || (order.user ? order.user.email : "vaibhav@gmail.com");
  const customerPhone = order.customerPhone || (order.user ? order.user.phone : "6353229708");
  const invoiceNumber = order.invoiceNumber || order.orderNumber || order._id || "sub_ee8f11169a_1775649192716";
  
  const paymentStatus = order.paymentStatus || "CREATED";
  const provider = order.provider || order.paymentProvider || "RAZORPAY";
  const currency = order.currency || "INR";
  const orderId = order.orderNumber || order._id || "order_SazN1curP4rq11";
  const paymentId = order.paymentId || "N/A";

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Invoice ${invoiceNumber}</title>
    <style>
      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
        color: #1e293b;
        background-color: #ffffff;
        padding: 0;
        margin: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .invoice-container {
        max-width: 800px;
        margin: 0 auto;
        background: #ffffff;
      }

      /* Header Banner */
      .header-banner {
        background-color: #0252b3;
        padding: 18px 32px;
        display: flex;
        justify-content: space-between;
        border-radius: 0;
        align-items: center;
      }
      .brand-section {
        display: flex;
        align-items: center;
      }
      .brand-copy {
        margin-left: 14px;
        color: #ffffff;
      }
      .brand-name {
        font-size: 21px;
        line-height: 1.15;
        font-weight: 800;
        letter-spacing: 0.3px;
      }
      .brand-tagline {
        margin-top: 5px;
        font-size: 11px;
        line-height: 1.25;
        color: #dbeafe;
      }
      .logo-box {
        width: 155px;
        height: 72px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #ffffff;
        border-radius: 4px;
        padding: 4px;
        overflow: hidden;
      }
      .logo-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
      }
      .invoice-title-block {
        text-align: right;
      }
      .invoice-title {
        font-size: 26px;
        font-weight: 800;
        letter-spacing: 1px;
        color: #ffffff;
      }
      .invoice-subtitle {
        font-size: 11px;
        color: #cbd5e1;
        margin-top: 2px;
      }

      /* Main Content Body */
      .content-body {
        padding: 32px 32px 24px;
      }

      /* Billing Info Grid */
      .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1.1fr;
        gap: 16px;
        margin-bottom: 32px;
      }
      .section-label {
        font-size: 11px;
        font-weight: 700;
        color: #0252b3;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 6px;
      }
      .info-block p {
        font-size: 13px;
        line-height: 1.5;
        color: #334155;
      }
      .info-block strong {
        color: #0f172a;
        font-size: 14px;
      }
      .meta-item {
        margin-bottom: 10px;
      }
      .meta-label {
        font-size: 11px;
        color: #64748b;
        margin-bottom: 1px;
      }
      .meta-value {
        font-size: 12.5px;
        font-weight: 700;
        color: #0f172a;
        word-break: break-all;
      }

      /* Items Table */
      .items-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
      }
      .items-table th {
        background-color: #0252b3;
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        padding: 10px 14px;
        text-align: left;
      }
      .items-table td {
        padding: 12px 14px;
        border: 1px solid #e2e8f0;
        font-size: 13px;
      }

      /* Totals Section */
      .totals-container {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 10px;
        margin-bottom: 32px;
      }
      .subtotal-row {
        display: flex;
        justify-content: space-between;
        width: 280px;
        font-size: 13.5px;
        color: #475569;
        padding: 0 4px;
      }
      .subtotal-amount {
        font-weight: 700;
        color: #0f172a;
      }
      .paid-card {
        width: 280px;
        background-color: #4cd5e6;
        color: #042f3a;
        padding: 10px 16px;
        border-radius: 2px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 700;
        font-size: 15px;
      }

      /* Payment Details Box */
      .payment-details-box {
        background-color: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 16px 20px;
        margin-bottom: 32px;
      }
      .payment-title {
        font-size: 12.5px;
        font-weight: 700;
        color: #0252b3;
        margin-bottom: 10px;
      }
      .payment-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
      }
      .pay-item-label {
        font-size: 10.5px;
        color: #64748b;
        margin-bottom: 2px;
      }
      .pay-item-value {
        font-size: 12.5px;
        font-weight: 700;
        color: #0f172a;
      }

      /* Footer */
      .footer-note {
        text-align: center;
        border-top: 1px solid #e2e8f0;
        padding-top: 20px;
        margin-top: 20px;
        font-size: 11.5px;
        color: #64748b;
        line-height: 1.5;
      }
      .footer-note .thankyou {
        font-style: italic;
        margin-top: 2px;
      }

      @media print {
        body {
          background: #ffffff;
        }
        .invoice-container {
          box-shadow: none;
        }
      }
    </style>
  </head>
  <body>
    <div class="invoice-container">
      <!-- Header Banner -->
      <div class="header-banner">
        <div class="brand-section">
          <div class="logo-box">
            <img src="${logoUrl}" alt="Medzeo Lab Logo" class="logo-img" />
          </div>
          <div class="brand-copy">
            <div class="brand-name">Medzeo Lab</div>
            <div class="brand-tagline">Fast &amp; Reliable Lab Tests</div>
          </div>
        </div>
        <div class="invoice-title-block">
          <div class="invoice-title">INVOICE</div>
          <div class="invoice-subtitle">Original for Recipient</div>
        </div>
      </div>

      <div class="content-body">
        <!-- Billing Details Grid -->
        <div class="info-grid">
          <div class="info-block">
            <div class="section-label">BILL FROM</div>
            <p><strong>Medzeo Lab</strong></p>
            <p>#98, Old Housing Board, Court Road,</p>
            <p>Bhiwani, Haryana - 127021</p>
            <p>GSTIN: 06AHKPS5751M1ZO</p>
          </div>

          <div class="info-block">
            <div class="section-label">BILL TO</div>
            <p><strong>${customerName}</strong></p>
            <p>${customerEmail} ${customerPhone}</p>
          </div>

          <div class="info-block">
            <div class="meta-item">
              <div class="meta-label">Invoice No.</div>
              <div class="meta-value">${invoiceNumber}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Invoice Date</div>
              <div class="meta-value">${formattedDate}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Payment Status</div>
              <div class="meta-value">${paymentStatus}</div>
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 60px; text-align: center;">Sr. No.</th>
              <th>Description</th>
              <th style="width: 130px;">Discount</th>
              <th style="width: 130px; text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${rows || `<tr><td colspan="4" style="text-align: center;">No items</td></tr>`}
          </tbody>
        </table>

        <!-- Totals Section -->
        <div class="totals-container">
          <div class="subtotal-row">
            <span>Subtotal</span>
            <span class="subtotal-amount">${formatCurrency(subtotal)}</span>
          </div>
          <div class="subtotal-row">
            <span>Discount</span>
            <span class="subtotal-amount">- ${formatCurrency(catalogueDiscount)}</span>
          </div>
          ${couponDiscount > 0 ? `<div class="subtotal-row"><span>Coupon Discount</span><span class="subtotal-amount">- ${formatCurrency(couponDiscount)}</span></div>` : ""}
          ${Object.entries(gstBySlab).sort(([a], [b]) => Number(a) - Number(b)).map(([slab, amount]) => `<div class="subtotal-row"><span>GST included (${slab}%)</span><span class="subtotal-amount">${formatCurrency(amount)}</span></div>`).join("")}
          <div class="paid-card">
            <span>Total Paid</span>
            <span>${formatCurrency(finalAmount)}</span>
          </div>
        </div>

        <!-- Payment Details Box -->
        <div class="payment-details-box">
          <div class="payment-title">Payment Details</div>
          <div class="payment-grid">
            <div>
              <div class="pay-item-label">Provider</div>
              <div class="pay-item-value">${provider}</div>
            </div>
            <div>
              <div class="pay-item-label">Currency</div>
              <div class="pay-item-value">${currency}</div>
            </div>
            <div>
              <div class="pay-item-label">Order ID</div>
              <div class="pay-item-value">${orderId}</div>
            </div>
            <div>
              <div class="pay-item-label">Payment ID</div>
              <div class="pay-item-value">${paymentId}</div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="footer-note">
          <p>This is a computer-generated invoice and does not require a signature.</p>
          <p class="thankyou">Thank you for using Medzeo Lab</p>
        </div>
      </div>
    </div>
  </body>
</html>`;
};
